import SupabaseModelService, { useModels } from '../models/supabaseModelService';
import { useMemo } from 'react';
import { Model } from '@shiftly/shared';

/**
 * Helper function to get the full name of a model from its ID
 * @param modelId - The ID of the model
 * @returns Promise<string | null> - The full name of the model, or null if not found
 */
export async function getModelFullName(modelId: string): Promise<string | null> {
  try {
    const model = await SupabaseModelService.getModel(modelId);
    return model?.name || null;
  } catch (error) {
    console.error('Error fetching model name:', error);
    return null;
  }
}

/**
 * Helper function to get multiple model names from their IDs
 * @param modelIds - Array of model IDs
 * @returns Promise<Record<string, string>> - Object mapping model IDs to their names
 */
export async function getModelNames(modelIds: string[]): Promise<Record<string, string>> {
  try {
    const models = await SupabaseModelService.getModels();
    const modelMap: Record<string, string> = {};
    
    modelIds.forEach(id => {
      const model = models.find(m => m.id === id);
      if (model) {
        modelMap[id] = model.name;
      }
    });
    
    return modelMap;
  } catch (error) {
    console.error('Error fetching model names:', error);
    return {};
  }
}

/**
 * Helper function to get a model's full name synchronously from a cached models array
 * @param modelId - The ID of the model
 * @param models - Array of models to search in
 * @returns string | null - The full name of the model, or null if not found
 */
export function getModelFullNameFromCache(modelId: string, models: Model[]): string | null {
  const model = models.find(m => m.id === modelId);
  return model?.name || null;
}

/**
 * React hook to get a model's full name from its ID
 * @param modelId - The ID of the model
 * @returns string | null - The full name of the model, or null if not found
 */
export function useModelFullName(modelId: string): string | null {
  const { data: models = [] } = useModels();
  
  return useMemo(() => {
    if (!modelId || !models.length) return null;
    return getModelFullNameFromCache(modelId, models);
  }, [modelId, models]);
}

/**
 * React hook to get multiple model names from their IDs
 * @param modelIds - Array of model IDs
 * @returns Record<string, string> - Object mapping model IDs to their names
 */
export function useModelNames(modelIds: string[]): Record<string, string> {
  const { data: models = [] } = useModels();
  
  return useMemo(() => {
    if (!modelIds.length || !models.length) return {};
    
    const modelMap: Record<string, string> = {};
    modelIds.forEach(id => {
      const model = models.find(m => m.id === id);
      if (model) {
        modelMap[id] = model.name;
      }
    });
    
    return modelMap;
  }, [modelIds, models]);
}
